import { logger } from '@heidi/runtime/logger';
import { TemplateCategory, TemplateVisibility } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type EditTemplateType,
  type TemplateResponse,
  GET_CONSULT_NOTE_TEMPLATES,
  GET_DOCUMENT_TEMPLATES,
  GET_ORGANIZATION_TEMPLATES,
  GET_TEMPLATE_BY_ID,
  GET_TEMPLATES,
} from '../../contracts';
import { createTemplate, editTemplate } from '../api/templates';
import { getUpdateTemplateSavePlan, willTemplateSaveCreate } from '../templateSavePlan';
import { getTeamIdForTemplateVisibility } from '../templateVisibilitySharing';

export function mergeTemplateIntoList(
  templates: TemplateResponse[] | undefined,
  updatedTemplate: TemplateResponse
) {
  if (!templates?.some((template) => template.id === updatedTemplate.id)) {
    return templates;
  }

  return templates.map((template) =>
    template.id === updatedTemplate.id ? { ...template, ...updatedTemplate } : template
  );
}

/**
 * `changedBy` is ANALYTICS-ONLY — it never reaches the request. Its union
 * (`OrganizationTemplateChangedBy`) is owned by `@/app-shell/settings/organization`,
 * which this package cannot import, so it crosses as a type parameter and the
 * delegate binds the app enum. Default `never`: unbound callers cannot pass one.
 */
export type UpdateTemplateVariables<TChangedBy = never> = {
  template: EditTemplateType;
  saveAnyway: boolean;
  changedBy?: TChangedBy;
  signal?: AbortSignal;
  /** Form-wide fill prompt: omitted keeps the stored one, `''` clears it. Kept off
   * `template` so forwarding a fetched template never resends a stored prompt. */
  globalPrompt?: string;
};

type UseMutateUpdateTemplateProps<TChangedBy, TPrevious> = {
  /**
   * Gates the organization-list refetch, exactly as it did before the carve.
   * A parameter, not an import: `useCombinedOrganizationInfo` stays app-side by
   * ratified disposition (workspace OT3), so there is nothing to wait for.
   */
  isOrganizationTemplateManager: boolean;
  /**
   * Supplies `onMutate`'s snapshot of the row as it was before the edit. The
   * legacy hook read it from `useOrganizationTemplatesQuery`, a
   * `@/app-shell/settings/organization` query with no package home — so the LOOKUP
   * crosses as a port and the snapshot itself stays opaque here. This package
   * never inspects it; it only carries it to `onBeforeCacheSync`, which is why
   * its type is a parameter rather than a shape this package has to know.
   */
  getPreviousTemplate?: (templateId?: string) => TPrevious | undefined;
  /**
   * Fires at the TOP of `onSuccess`, before the cache is touched — the position
   * the global visibility-changed dispatch occupied. That comparison needs the
   * previous snapshot and a deep-equality check on `shared_team_ids`, and it
   * ends in an analytics dispatch, so all of it is an app reaction.
   */
  onBeforeCacheSync?: (
    template: TemplateResponse,
    variables: UpdateTemplateVariables<TChangedBy>,
    previousTemplate: TPrevious | undefined
  ) => void;
  /**
   * Fires after every invalidation has settled — the position of the three
   * remaining analytics dispatches and the success toast.
   */
  onUpdated?: (
    template: TemplateResponse,
    variables: UpdateTemplateVariables<TChangedBy>
  ) => void;
  /**
   * Fires in `onError`. The legacy body was one call to
   * `handleTemplateComplianceError`, which needs `dispatch`, `intl`, `toast`
   * and three modal atoms — an app reaction end to end.
   */
  onUpdateFailed?: (
    error: unknown,
    variables: UpdateTemplateVariables<TChangedBy>
  ) => void;
};

/**
 * Saves a template edit — as an edit when the save plan persists in place, and
 * as a NEW template otherwise (`willTemplateSaveCreate`: a non-private template
 * is copied rather than mutated), then reconciles every list it can appear in.
 *
 * Headless. The one app fact it keeps is `isOrganizationTemplateManager`, and
 * only because it gates a TEMPLATES cache invalidation; every other use of it
 * lived inside an analytics branch and went app-side with that branch.
 */
export function useMutateUpdateTemplate<TChangedBy = never, TPrevious = unknown>({
  isOrganizationTemplateManager,
  getPreviousTemplate,
  onBeforeCacheSync,
  onUpdated,
  onUpdateFailed,
}: UseMutateUpdateTemplateProps<TChangedBy, TPrevious>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      template,
      saveAnyway,
      changedBy,
      signal,
      globalPrompt,
    }: UpdateTemplateVariables<TChangedBy>) => {
      logger.debug('changedBy', changedBy);
      const savePlan = getUpdateTemplateSavePlan(template?.is_private);
      if (template && !willTemplateSaveCreate(savePlan)) {
        // edit existing template
        const data = await editTemplate({
          template: {
            name: template?.name,
            // `isEmpty` (lodash-es) cannot enter package source — ESM-only, and
            // the package jest preset is transform-only. Every field it guarded
            // here is typed `string | undefined`, and on those two types
            // `isEmpty(v)` and `!v` agree on every input, `'0'` included.
            structure_template: !template?.structure_template
              ? undefined
              : template?.structure_template,
            template_html: !template?.template_html ? undefined : template?.template_html,
            template_visibility: template?.template_visibility,
            template_category: template.template_category,
            team_id: getTeamIdForTemplateVisibility({
              visibility: template.template_visibility,
              templateTeamId: template?.team_id,
              fallbackTeamId: undefined,
            }),
            template_gallery_sharing_preference_show_name:
              template?.template_gallery_sharing_preference_show_name,
            template_gallery_sharing_preference_show_clinic:
              template?.template_gallery_sharing_preference_show_clinic,
            template_gallery_sharing_preference_show_profile_picture:
              template?.template_gallery_sharing_preference_show_profile_picture,
            sectional_note_ehr_provider: template?.sectional_note_ehr_provider,
            shared_team_ids: template?.shared_team_ids,
            version_control_id: template?.version_control_id || undefined,
            convert_to_json: template?.convert_to_json ?? false,
            original_v1_structure_template:
              template?.original_v1_structure_template || undefined,
            form_fields: template?.form_fields,
            problem_based_charting_enabled:
              template?.problem_based_charting_enabled ?? false,
            // This request is a PATCH, so an absent key preserves the stored
            // value while a literal `false` overwrites it. The v2 builder form
            // deliberately sends a concrete category-coerced boolean on every
            // save — the backend stores a plain bool where a missing key reads
            // back as `false`, so explicit `false` is not a distinct state.
            // The coalesce is load-bearing for callers that forward a full
            // TemplateResponse: its `use_sections` is nullable, and `null`
            // must drop to `undefined` (omitted at serialisation, like
            // `version_control_id` above) rather than PATCH an explicit null.
            // The create arms default the field, which is why the POST
            // payloads below still send `false`.
            use_sections: template?.use_sections ?? undefined,
            ...(globalPrompt === undefined ? {} : { global_prompt: globalPrompt }),
          },
          templateId: template?.id || '',
          saveAnyway: saveAnyway,
          signal,
        });

        return data;
      }

      // create a new template
      const basePayload = {
        name: template?.name || '',
        template_visibility: template?.template_visibility || TemplateVisibility.User,
        description: template?.description,
        structure_template: !template?.structure_template
          ? undefined
          : template?.structure_template,
        template_html: !template?.template_html ? undefined : template?.template_html,
        template_category:
          template?.template_category || TemplateCategory.DOCUMENT_TEMPLATE,
        team_id: template?.team_id,
        template_gallery_sharing_preference_show_name:
          template?.template_gallery_sharing_preference_show_name,
        template_gallery_sharing_preference_show_clinic:
          template?.template_gallery_sharing_preference_show_clinic,
        template_gallery_sharing_preference_show_profile_picture:
          template?.template_gallery_sharing_preference_show_profile_picture,
        sectional_note_ehr_provider: template?.sectional_note_ehr_provider,
        version_control_id: template?.version_control_id || undefined,
        shared_team_ids: template?.shared_team_ids,
        convert_to_json: template?.convert_to_json ?? false,
        original_v1_structure_template:
          template?.original_v1_structure_template || undefined,
        problem_based_charting_enabled: template?.problem_based_charting_enabled ?? false,
        use_sections: template?.use_sections ?? false,
      };

      const templatePayload = template?.s3_object_key
        ? {
            ...basePayload,
            s3_object_key: template.s3_object_key,
            trigger: 'from_pdf' as const,
            ...(globalPrompt === undefined ? {} : { global_prompt: globalPrompt }),
          }
        : { ...basePayload, trigger: 'from_scratch' as const };

      return await createTemplate({
        template: templatePayload,
        saveAnyway: saveAnyway,
        signal,
      });
    },

    onMutate: ({ template }) => {
      const previousTemplate = getPreviousTemplate?.(template?.id);
      return { previousTemplate };
    },

    onSuccess: async ({ data: template }, variables, context) => {
      onBeforeCacheSync?.(template, variables, context?.previousTemplate);

      queryClient.setQueriesData<TemplateResponse[]>(
        { queryKey: [GET_DOCUMENT_TEMPLATES] },
        (cachedTemplates) => mergeTemplateIntoList(cachedTemplates, template)
      );

      const shouldSoftInvalidateDocumentTemplates = template.form_fields != null;

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_TEMPLATES],
        }),
        queryClient.invalidateQueries(
          shouldSoftInvalidateDocumentTemplates
            ? {
                queryKey: [GET_DOCUMENT_TEMPLATES],
                refetchType: 'none',
              }
            : {
                queryKey: [GET_DOCUMENT_TEMPLATES],
              }
        ),
        queryClient.invalidateQueries({
          queryKey: [GET_CONSULT_NOTE_TEMPLATES],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_TEMPLATE_BY_ID, { templateId: variables?.template?.id }],
        }),
      ]);
      if (isOrganizationTemplateManager) {
        await queryClient.invalidateQueries({
          queryKey: [GET_ORGANIZATION_TEMPLATES],
        });
      }

      onUpdated?.(template, variables);
    },

    onError: (error, variables) => {
      onUpdateFailed?.(error, variables);
    },
  });
}
